"use client";

import type { ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import InstagramIcon from "@mui/icons-material/Instagram";
import Header from "@/app/components/header";
import Footer from "@/app/components/footer";
import { MagicLayoutHelmet } from "@/app/(public)/_seo/PublicLayoutMetadata";
import ScrollReveal from "@/app/components/ScrollReveal";
import {
  MAGIC_BORDER,
  MAGIC_CLOSING,
  MAGIC_GALLERY,
  MAGIC_HERO,
  MAGIC_IMAGES,
  MAGIC_MUTED,
  MAGIC_MUTED_SOFT,
  MAGIC_PAGE_BG,
  MAGIC_PAGE_FONT,
  MAGIC_PRESS,
  MAGIC_WAYS,
  type MagicGalleryItem,
} from "./magicContent";
import {
  trackMagicBookEventClick,
  trackMagicInstagramClick,
  trackMagicShowsClick,
} from "./magicAnalytics";

const reducedMotionSx = {
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none !important",
    animation: "none !important",
    transform: "none !important",
  },
} as const;

const eyebrowSx = {
  fontFamily: MAGIC_PAGE_FONT,
  fontSize: { xs: "0.6875rem", sm: "0.75rem" },
  fontWeight: 600,
  letterSpacing: "0.16em",
  textTransform: "uppercase" as const,
  color: MAGIC_MUTED_SOFT,
  lineHeight: 1.2,
};

function CtaButton({
  to,
  label,
  variant,
  onClick,
  external,
  startIcon,
  fullWidthOnMobile = true,
}: {
  to: string;
  label: string;
  variant: "primary" | "secondary";
  onClick?: () => void;
  external?: boolean;
  startIcon?: ReactNode;
  fullWidthOnMobile?: boolean;
}) {
  const isPrimary = variant === "primary";
  const sharedSx = {
    fontFamily: MAGIC_PAGE_FONT,
    fontWeight: 700,
    fontSize: { xs: "0.9375rem", sm: "1rem" },
    textTransform: "none" as const,
    borderRadius: "14px",
    px: { xs: 2.75, sm: 3.25 },
    py: { xs: 1.35, sm: 1.45 },
    minHeight: { xs: 48, sm: 52 },
    width: fullWidthOnMobile ? { xs: "100%", sm: "auto" } : "auto",
    boxShadow: "none",
    WebkitTapHighlightColor: "transparent",
    touchAction: "manipulation",
    ...reducedMotionSx,
    transition: "background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease",
    "&:focus-visible": {
      outline: "2px solid #fff",
      outlineOffset: 3,
    },
  };

  if (external) {
    return (
      <Button
        component="a"
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick}
        startIcon={startIcon}
        disableElevation
        sx={{
          ...sharedSx,
          ...(isPrimary
            ? {
                backgroundColor: "#f2f2f2",
                color: MAGIC_PAGE_BG,
                "&:hover": { backgroundColor: "#fff", boxShadow: "none" },
              }
            : {
                backgroundColor: "transparent",
                color: "#fff",
                border: `1px solid ${MAGIC_BORDER}`,
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.06)",
                  borderColor: "rgba(255,255,255,0.35)",
                  boxShadow: "none",
                },
              }),
        }}
      >
        {label}
      </Button>
    );
  }

  return (
    <Button
      component={RouterLink}
      to={to}
      onClick={onClick}
      startIcon={startIcon}
      disableElevation
      sx={{
        ...sharedSx,
        ...(isPrimary
          ? {
              backgroundColor: "#f2f2f2",
              color: MAGIC_PAGE_BG,
              "&:hover": { backgroundColor: "#fff", boxShadow: "none" },
            }
          : {
              backgroundColor: "transparent",
              color: "#fff",
              border: `1px solid ${MAGIC_BORDER}`,
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.06)",
                borderColor: "rgba(255,255,255,0.35)",
                boxShadow: "none",
              },
            }),
      }}
    >
      {label}
    </Button>
  );
}

function WayCardIcon({ id }: { id: string }) {
  const sx = { fontSize: 18, color: "rgba(255,255,255,0.85)" };
  if (id === "private-events") return <BusinessOutlinedIcon sx={sx} />;
  return <ConfirmationNumberOutlinedIcon sx={sx} />;
}

function GalleryCell({ item }: { item: MagicGalleryItem }) {
  const radius = "14px";
  const aspect =
    item.span === "feature"
      ? { xs: "4 / 5", md: "unset" }
      : item.span === "wide"
        ? { xs: "16 / 10", md: "unset" }
        : { xs: "1 / 1", md: "unset" };

  if (item.kind === "more") {
    return (
      <Box
        sx={{
          borderRadius: radius,
          border: `1px solid ${MAGIC_BORDER}`,
          backgroundColor: "rgba(255,255,255,0.06)",
          backdropFilter: "blur(14px) saturate(1.1)",
          WebkitBackdropFilter: "blur(14px) saturate(1.1)",
          aspectRatio: aspect,
          height: { md: "100%" },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          px: 3,
          textAlign: "center",
          minHeight: 0,
          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
        }}
      >
        <Typography
          sx={{
            fontFamily: MAGIC_PAGE_FONT,
            fontSize: { xs: "1.15rem", md: "1.35rem" },
            fontWeight: 700,
            letterSpacing: "-0.02em",
            color: "#fff",
            lineHeight: 1.2,
          }}
        >
          {item.label}
        </Typography>
        {item.sublabel ? (
          <Typography
            sx={{
              fontFamily: MAGIC_PAGE_FONT,
              fontSize: "0.8125rem",
              lineHeight: 1.45,
              color: MAGIC_MUTED_SOFT,
              maxWidth: 260,
            }}
          >
            {item.sublabel}
          </Typography>
        ) : null}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: radius,
        overflow: "hidden",
        border: `1px solid ${MAGIC_BORDER}`,
        aspectRatio: aspect,
        height: { md: "100%" },
        width: "100%",
        backgroundColor: "#111",
        minHeight: { xs: 0, md: 0 },
      }}
    >
      <Box
        component="img"
        src={item.src}
        alt={item.alt}
        loading="lazy"
        decoding="async"
        sx={{
          display: "block",
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition: "center",
          filter: item.mono ? "grayscale(1)" : "none",
          transition: "transform 0.45s ease",
          ...reducedMotionSx,
          ".gallery-cell:hover &": {
            transform: "scale(1.03)",
          },
          "@media (prefers-reduced-motion: reduce)": {
            ".gallery-cell:hover &": { transform: "none" },
          },
        }}
      />
      {item.caption ? (
        <Box
          component="span"
          sx={{
            position: "absolute",
            left: 12,
            bottom: 12,
            px: 1.25,
            py: 0.55,
            borderRadius: "8px",
            backgroundColor: "rgba(10,10,10,0.55)",
            border: `1px solid ${MAGIC_BORDER}`,
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
          }}
        >
          <Typography
            component="span"
            sx={{
              fontFamily: MAGIC_PAGE_FONT,
              fontSize: "0.625rem",
              fontWeight: 600,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.9)",
              lineHeight: 1.35,
            }}
          >
            {item.caption}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
}

export default function MagicPage() {
  return (
    <>
      <MagicLayoutHelmet />
      <Box
        sx={{
          minHeight: "100vh",
          width: "100%",
          maxWidth: "100%",
          overflowX: "hidden",
          boxSizing: "border-box",
          bgcolor: MAGIC_PAGE_BG,
          color: "#fff",
          WebkitTextSizeAdjust: "100%",
        }}
      >
        <Header variant="dark" />
        <Box component="main">

        {/* —— Hero —— */}
        <Box
          component="section"
          aria-label="Magic by ComedyUO"
          sx={{
            position: "relative",
            width: "100%",
            minHeight: { xs: "min(88vh, 720px)", md: "min(92vh, 860px)" },
            display: "flex",
            alignItems: "center",
            backgroundImage: `url("${MAGIC_IMAGES.hero.src}")`,
            backgroundSize: "cover",
            backgroundPosition: { xs: "center 30%", md: "center" },
            backgroundRepeat: "no-repeat",
          }}
        >
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              inset: 0,
              background: {
                xs: "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.58) 40%, rgba(10,10,10,0.85) 85%, #0a0a0a 100%), linear-gradient(90deg, rgba(20,12,8,0.35) 0%, transparent 55%)",
                md: "linear-gradient(90deg, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.45) 48%, rgba(0,0,0,0.35) 100%), linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.25) 40%, rgba(10,10,10,0.55) 80%, #0a0a0a 100%), linear-gradient(90deg, rgba(28,16,10,0.28) 0%, transparent 50%)",
              },
              zIndex: 0,
            }}
          />
          <Container
            maxWidth="lg"
            sx={{
              position: "relative",
              zIndex: 1,
              width: "100%",
              pl: {
                xs: "max(16px, env(safe-area-inset-left, 0px))",
                sm: 3,
                md: 4,
              },
              pr: {
                xs: "max(16px, env(safe-area-inset-right, 0px))",
                sm: 3,
                md: 4,
              },
              pt: { xs: 8, md: 10 },
              pb: { xs: 8, md: 10 },
              boxSizing: "border-box",
            }}
          >
            <Box sx={{ maxWidth: 720 }}>
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.5,
                  py: 0.7,
                  mb: { xs: 2, md: 2.5 },
                  borderRadius: "999px",
                  border: `1px solid ${MAGIC_BORDER}`,
                  backgroundColor: "rgba(12,12,14,0.55)",
                  backdropFilter: "blur(10px)",
                }}
              >
                <AutoAwesomeOutlinedIcon
                  sx={{ fontSize: 14, color: "rgba(255,255,255,0.85)" }}
                />
                <Typography component="span" sx={{ ...eyebrowSx, color: "rgba(255,255,255,0.88)" }}>
                  {MAGIC_HERO.eyebrow}
                </Typography>
              </Box>

              <Typography
                component="h1"
                sx={{
                  fontFamily: MAGIC_PAGE_FONT,
                  fontWeight: 700,
                  fontSize: {
                    xs: "clamp(2.35rem, 9vw, 3.4rem)",
                    md: "clamp(3.5rem, 5.5vw, 4.75rem)",
                  },
                  lineHeight: { xs: 1.05, md: 1.02 },
                  letterSpacing: "-0.03em",
                  color: "#fff",
                  mb: { xs: 2, md: 2.25 },
                  maxWidth: 720,
                  wordBreak: "normal",
                }}
              >
                {MAGIC_HERO.headlineLead}{" "}
                <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                  {MAGIC_HERO.headlineEmphasis}
                </Box>
              </Typography>

              <Typography
                sx={{
                  fontFamily: MAGIC_PAGE_FONT,
                  fontSize: { xs: "0.975rem", sm: "1.05rem", md: "1.125rem" },
                  lineHeight: 1.6,
                  color: MAGIC_MUTED,
                  maxWidth: 540,
                  mb: { xs: 3, md: 3.5 },
                }}
              >
                {MAGIC_HERO.body}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  flexWrap: "wrap",
                  gap: 1.5,
                }}
              >
                <CtaButton
                  to={MAGIC_HERO.primaryCta.href}
                  label={`${MAGIC_HERO.primaryCta.label} →`}
                  variant="primary"
                  onClick={trackMagicBookEventClick}
                />
                <CtaButton
                  to={MAGIC_HERO.secondaryCta.href}
                  label={MAGIC_HERO.secondaryCta.label}
                  variant="secondary"
                  onClick={trackMagicShowsClick}
                />
              </Box>
            </Box>
          </Container>
        </Box>

        {/* —— Ways to work —— */}
        <Box
          component="section"
          aria-labelledby="magic-ways-heading"
          sx={{
            py: { xs: 7, md: 10 },
            px: { xs: 0, md: 0 },
          }}
        >
          <Container
            maxWidth="lg"
            sx={{
              px: {
                xs: "max(16px, env(safe-area-inset-left, 0px))",
                sm: 3,
                md: 4,
              },
              boxSizing: "border-box",
            }}
          >
            <ScrollReveal>
              <Box sx={{ mb: { xs: 3.5, md: 4.5 }, maxWidth: 820 }}>
                <Typography sx={{ ...eyebrowSx, mb: 1.5 }}>
                  {MAGIC_WAYS.eyebrow}
                </Typography>
                <Typography
                  id="magic-ways-heading"
                  component="h2"
                  sx={{
                    fontFamily: MAGIC_PAGE_FONT,
                    fontWeight: 700,
                    fontSize: {
                      xs: "clamp(1.55rem, 5.5vw, 2.2rem)",
                      md: "clamp(2.1rem, 3vw, 2.85rem)",
                    },
                    lineHeight: 1.15,
                    letterSpacing: "-0.025em",
                    color: "#fff",
                    whiteSpace: { sm: "nowrap" },
                    mb: 1.25,
                  }}
                >
                  {MAGIC_WAYS.headline}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: MAGIC_PAGE_FONT,
                    fontSize: { xs: "0.95rem", md: "1.05rem" },
                    lineHeight: 1.55,
                    color: MAGIC_MUTED,
                    fontStyle: "italic",
                  }}
                >
                  {MAGIC_WAYS.body}
                </Typography>
              </Box>
            </ScrollReveal>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "1fr 1fr",
                },
                gap: { xs: 2, md: 2.25 },
              }}
            >
              {MAGIC_WAYS.cards.map((card, index) => {
                const isImage = card.kind === "image";

                return (
                  <ScrollReveal key={card.id} delay={0.06 * index}>
                    <Box
                      sx={{
                        position: "relative",
                        borderRadius: { xs: "16px", md: "18px" },
                        overflow: "hidden",
                        border: `1px solid ${MAGIC_BORDER}`,
                        backgroundColor: isImage ? "#111" : "rgba(28,28,32,0.92)",
                        minHeight: { xs: 420, md: 480 },
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: isImage ? "flex-end" : "flex-start",
                        p: { xs: 2.5, sm: 3, md: 3.25 },
                        boxSizing: "border-box",
                        height: "100%",
                      }}
                    >
                      {isImage && card.image ? (
                        <>
                          <Box
                            component="img"
                            src={card.image.src}
                            alt={card.image.alt}
                            loading="lazy"
                            decoding="async"
                            sx={{
                              position: "absolute",
                              inset: 0,
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              objectPosition: "center",
                            }}
                          />
                          <Box
                            aria-hidden
                            sx={{
                              position: "absolute",
                              inset: 0,
                              background:
                                "linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.45) 40%, rgba(0,0,0,0.88) 100%)",
                            }}
                          />
                        </>
                      ) : null}

                      <Box sx={{ position: "relative", zIndex: 1 }}>
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 1.75,
                          }}
                        >
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: "50%",
                              border: `1px solid ${MAGIC_BORDER}`,
                              display: "grid",
                              placeItems: "center",
                              backgroundColor: "rgba(0,0,0,0.25)",
                            }}
                          >
                            <WayCardIcon id={card.id} />
                          </Box>
                          <Typography sx={{ ...eyebrowSx, color: "rgba(255,255,255,0.78)" }}>
                            {card.label}
                          </Typography>
                        </Box>

                        <Typography
                          component="h3"
                          sx={{
                            fontFamily: MAGIC_PAGE_FONT,
                            fontWeight: 700,
                            fontSize: {
                              xs: "1.35rem",
                              md: isImage ? "1.75rem" : "1.35rem",
                            },
                            lineHeight: 1.2,
                            letterSpacing: "-0.02em",
                            color: "#fff",
                            mb: 1.25,
                          }}
                        >
                          {card.headline}
                        </Typography>

                        <Typography
                          sx={{
                            fontFamily: MAGIC_PAGE_FONT,
                            fontSize: { xs: "0.9rem", md: "0.95rem" },
                            lineHeight: 1.55,
                            color: MAGIC_MUTED,
                            mb: 2.25,
                            maxWidth: 480,
                          }}
                        >
                          {card.body}
                        </Typography>

                        <Box
                          component={RouterLink}
                          to={card.cta.href}
                          onClick={() => {
                            if (card.id === "private-events") {
                              trackMagicBookEventClick();
                            } else {
                              trackMagicShowsClick();
                            }
                          }}
                          sx={{
                            fontFamily: MAGIC_PAGE_FONT,
                            fontWeight: 700,
                            fontSize: "0.9375rem",
                            color: "#fff",
                            textDecoration: "none",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            minHeight: 44,
                            ...reducedMotionSx,
                            transition: "opacity 0.2s ease",
                            "&:hover": { opacity: 0.85 },
                            "&:focus-visible": {
                              outline: "2px solid #fff",
                              outlineOffset: 3,
                              borderRadius: 4,
                            },
                          }}
                        >
                          {card.cta.label} →
                        </Box>
                      </Box>
                    </Box>
                  </ScrollReveal>
                );
              })}
            </Box>
          </Container>
        </Box>

        {/* —— Image reel —— */}
        <Box
          component="section"
          aria-labelledby="magic-reel-heading"
          sx={{ pb: { xs: 7, md: 10 } }}
        >
          <Container
            maxWidth="lg"
            sx={{
              px: {
                xs: "max(16px, env(safe-area-inset-left, 0px))",
                sm: 3,
                md: 4,
              },
              boxSizing: "border-box",
            }}
          >
            <ScrollReveal>
              <Typography
                id="magic-reel-heading"
                component="h2"
                sx={{
                  fontFamily: MAGIC_PAGE_FONT,
                  fontWeight: 700,
                  fontSize: {
                    xs: "clamp(1.75rem, 6.5vw, 2.35rem)",
                    md: "clamp(2.25rem, 3.2vw, 3rem)",
                  },
                  lineHeight: 1.12,
                  letterSpacing: "-0.025em",
                  color: "#fff",
                  mb: { xs: 3, md: 4 },
                }}
              >
                {MAGIC_GALLERY.headline}
              </Typography>
            </ScrollReveal>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                  md: "1.2fr 0.9fr 0.9fr",
                },
                gridTemplateRows: {
                  md: "minmax(0, 1fr) minmax(0, 1fr)",
                },
                gridAutoRows: { xs: "auto", sm: "auto" },
                alignItems: "stretch",
                gap: { xs: 1.5, md: 1.75 },
                minHeight: { md: 560 },
              }}
            >
              {MAGIC_GALLERY.items.map((item, index) => {
                const placement =
                  index === 0
                    ? {
                        gridColumn: { md: "1" },
                        gridRow: { md: "1 / span 2" },
                      }
                    : index === 1
                      ? { gridColumn: { md: "2" }, gridRow: { md: "1" } }
                      : index === 2
                        ? { gridColumn: { md: "3" }, gridRow: { md: "1" } }
                        : index === 3
                          ? { gridColumn: { md: "2" }, gridRow: { md: "2" } }
                          : { gridColumn: { md: "3" }, gridRow: { md: "2" } };

                return (
                  <Box
                    key={item.id}
                    className="gallery-cell"
                    sx={{
                      ...placement,
                      minWidth: 0,
                      minHeight: 0,
                      height: { md: "100%" },
                      display: "flex",
                      "& > *": { flex: 1, minHeight: 0 },
                      "&:hover img": {
                        transform: "scale(1.03)",
                      },
                      "@media (prefers-reduced-motion: reduce)": {
                        "&:hover img": { transform: "none" },
                      },
                    }}
                  >
                    <GalleryCell item={item} />
                  </Box>
                );
              })}
            </Box>
          </Container>
        </Box>

        {/* —— Press quote —— */}
        <Box
          component="section"
          aria-label="Press quote"
          sx={{
            position: "relative",
            py: { xs: 8, md: 11 },
            backgroundImage: `url("${MAGIC_IMAGES.quoteBg.src}")`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              inset: 0,
              backgroundColor: "rgba(0,0,0,0.72)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
            }}
          />
          <Container
            maxWidth="md"
            sx={{
              position: "relative",
              zIndex: 1,
              textAlign: "center",
              px: { xs: 2.5, sm: 3 },
            }}
          >
            <ScrollReveal variant="fadeIn">
              <FormatQuoteIcon
                sx={{
                  fontSize: 36,
                  color: "rgba(255,255,255,0.35)",
                  mb: 2,
                  transform: "scaleX(-1)",
                }}
              />
              <Typography
                sx={{
                  ...eyebrowSx,
                  mb: 2.5,
                  color: "rgba(255,255,255,0.7)",
                }}
              >
                {MAGIC_PRESS.publication}
              </Typography>
              <Typography
                component="a"
                href={MAGIC_PRESS.href}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: "block",
                  fontFamily: MAGIC_PAGE_FONT,
                  fontWeight: 700,
                  fontSize: {
                    xs: "clamp(1.15rem, 4.2vw, 1.65rem)",
                    md: "clamp(1.45rem, 2.4vw, 1.9rem)",
                  },
                  lineHeight: 1.3,
                  letterSpacing: "-0.02em",
                  color: "#fff",
                  m: 0,
                  mb: 3,
                  textDecoration: "none",
                  ...reducedMotionSx,
                  transition: "opacity 0.2s ease",
                  "&:hover": { opacity: 0.88 },
                  "&:focus-visible": {
                    outline: "2px solid #fff",
                    outlineOffset: 4,
                    borderRadius: 4,
                  },
                }}
              >
                “{MAGIC_PRESS.quote}”
              </Typography>
              <Box
                aria-hidden
                sx={{
                  width: 48,
                  height: 1,
                  mx: "auto",
                  backgroundColor: "rgba(255,255,255,0.35)",
                }}
              />
            </ScrollReveal>
          </Container>
        </Box>

        {/* —— Closing CTA —— */}
        <Box
          component="section"
          aria-labelledby="magic-closing-heading"
          sx={{
            position: "relative",
            py: { xs: 8, md: 11 },
            overflow: "hidden",
          }}
        >
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              left: "50%",
              top: "42%",
              transform: "translate(-50%, -50%)",
              width: { xs: 280, md: 420 },
              height: { xs: 280, md: 420 },
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(242,170,35,0.14) 0%, rgba(242,170,35,0.04) 45%, transparent 70%)",
              pointerEvents: "none",
            }}
          />
          <Container
            maxWidth="sm"
            sx={{
              position: "relative",
              zIndex: 1,
              textAlign: "center",
              px: { xs: 2.5, sm: 3 },
            }}
          >
            <ScrollReveal>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  mx: "auto",
                  mb: 2.5,
                  borderRadius: "12px",
                  border: `1px solid ${MAGIC_BORDER}`,
                  display: "grid",
                  placeItems: "center",
                  backgroundColor: "rgba(255,255,255,0.03)",
                }}
              >
                <InstagramIcon sx={{ fontSize: 22, color: "#fff" }} />
              </Box>
              <Typography
                id="magic-closing-heading"
                component="h2"
                sx={{
                  fontFamily: MAGIC_PAGE_FONT,
                  fontWeight: 700,
                  fontSize: {
                    xs: "clamp(1.65rem, 6.5vw, 2.25rem)",
                    md: "2.5rem",
                  },
                  lineHeight: 1.15,
                  letterSpacing: "-0.025em",
                  color: "#fff",
                  mb: 1.75,
                }}
              >
                {MAGIC_CLOSING.headline}
              </Typography>
              <Typography
                sx={{
                  fontFamily: MAGIC_PAGE_FONT,
                  fontSize: { xs: "0.95rem", md: "1.05rem" },
                  lineHeight: 1.55,
                  color: MAGIC_MUTED,
                  mb: 3.5,
                }}
              >
                {MAGIC_CLOSING.body}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "center",
                  gap: 1.5,
                }}
              >
                <CtaButton
                  to={MAGIC_CLOSING.primaryCta.href}
                  label={MAGIC_CLOSING.primaryCta.label}
                  variant="primary"
                  external
                  startIcon={<InstagramIcon />}
                  onClick={trackMagicInstagramClick}
                />
                <CtaButton
                  to={MAGIC_CLOSING.secondaryCta.href}
                  label={MAGIC_CLOSING.secondaryCta.label}
                  variant="secondary"
                  onClick={trackMagicBookEventClick}
                />
              </Box>
            </ScrollReveal>
          </Container>
        </Box>
        </Box>

        <Footer />
      </Box>
    </>
  );
}
